import { Checkbox, ClickAwayListener, Drawer, Radio, Typography } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';

import { KineticLayer, MapContext } from 'components/maps/MapContext';
import groupBy from 'lodash/groupBy';
import { useTranslation } from 'react-i18next';
import useLayersToolbarStyles from '../styles';
import Group from './Group';
import { LayersDrawerProps } from './types';

const LayersDrawer: React.FunctionComponent<LayersDrawerProps> = ({
  open,
  anchor,
  onClickAway,
  withoutToolbar,
}) => {
  const { t } = useTranslation('maps');
  const { selectedLayers, layers, setLayerSelected } = useContext(MapContext);
  const classes = useLayersToolbarStyles();
  const baseLayers = useMemo(() => layers.filter((layer) => layer.baseLayer), [layers]);

  function hasLayerSelected(layerId: string) {
    return selectedLayers.includes(layerId);
  }

  const onLayerClick = (layerId: string, allowDeselect: boolean = true) => {
    const layer = layers.find((layer) => layer.id === layerId);
    if (!layer) {
      return;
    }
    const group = layers.filter((toFilter) => layer.group === toFilter.group);

    if (layer.type === 'radio') {
      // don't allow deselection if only 1 radio or deselection is not allowed
      if (!allowDeselect && hasLayerSelected(layerId)) {
        return;
      }
      // if ts a radio button, deselect all other layers (which is also of type radio) in the group
      group.forEach((layer) => {
        if (layer.type === 'radio') {
          setLayerSelected(layer.id, false);
        }
      });
    }

    const layerSelected = !hasLayerSelected(layerId);

    layer.onLayerClick?.(layerSelected, selectedLayers);

    setLayerSelected(layerId, layerSelected);
  };

  const getInput = (type: 'radio' | 'checkbox', layerId: string) => {
    switch (type) {
      case 'radio':
        return <Radio size='small' className={classes.radio} checked={hasLayerSelected(layerId)} />;
      case 'checkbox':
        return (
          <Checkbox size='small' className={classes.radio} checked={hasLayerSelected(layerId)} />
        );
    }
  };

  const onGroupLayerClick = (checked: boolean, group: string) => {
    layers
      .filter(
        (layer) =>
          (layer.group === group || (!layer.group && group === t('overlays'))) &&
          layer.type === 'checkbox'
      )
      .forEach((layer) => {
        setLayerSelected(layer.id, checked);
      });

    if (!checked) {
      layers
        .filter((layer) => layer.group === group && layer.type === 'radio')
        .forEach((layer) => {
          setLayerSelected(layer.id, checked);
        });
      return;
    }

    const firstRadio = layers.find((layer) => layer.group === group && layer.type === 'radio');
    if (!firstRadio) {
      return;
    }

    layers
      .filter((layer) => layer.group === group && layer.type === 'radio')
      .forEach((layer) => {
        if (layer.id === firstRadio.id) {
          setLayerSelected(layer.id, true);
        } else {
          setLayerSelected(layer.id, false);
        }
      });
  };

  const renderGroupCheckbox = (groupName: string, includeUndefined?: boolean) => {
    const group = layers.filter(
      (layer) => layer.group === groupName || (includeUndefined && !layer.group)
    );

    let radioLayerSelected = false;
    let allCheckboxLayersSelected = true;

    group.forEach((layer) => {
      if (layer.type === 'radio' && hasLayerSelected(layer.id)) {
        radioLayerSelected = true;
      }
      if (layer.type === 'checkbox' && !hasLayerSelected(layer.id)) {
        allCheckboxLayersSelected = false;
      }
    });

    if (group.filter((group) => group.type === 'checkbox').length === 0) {
      allCheckboxLayersSelected = true;
    }

    if (group.filter((group) => group.type === 'radio').length === 0) {
      radioLayerSelected = true;
    }

    return (
      <Checkbox
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          onGroupLayerClick(event.target.checked, groupName);
        }}
        size='small'
        className={classes.radio}
        checked={radioLayerSelected && allCheckboxLayersSelected}
      />
    );
  };

  const renderLayer = (layer: KineticLayer, allowDeselect?: boolean) => {
    if (!layer) {
      return null;
    }
    return (
      <div
        key={layer.id}
        className={classes.radioLabel}
        onClick={() => {
          onLayerClick(layer.id, allowDeselect);
        }}
      >
        {getInput(layer.type, layer.id)}
        <Typography variant='subtitle1'>{layer.label ?? ''}</Typography>
      </div>
    );
  };

  const [openGroups, setOpenGroups] = useState<string[]>([]);

  const onToggle = (needle: string) => {
    setOpenGroups((prev) => {
      if (prev.includes(needle)) {
        return prev.filter((name) => name !== needle);
      }
      return [...prev, needle];
    });
  };

  let layerControlGroups: JSX.Element[] = [];

  if (baseLayers.length) {
    layerControlGroups.push(
      <Group
        id='baseLayerList'
        key='baseLayers'
        name='baseLayers'
        onToggle={onToggle}
        title='Base Layers'
        expanded={openGroups.includes('baseLayers')}
      >
        {baseLayers.map((layer) => renderLayer(layer, false))}
      </Group>
    );
  }

  const overlays = useMemo(
    () =>
      layers.filter((layer) => !layer.baseLayer && (!layer.group || layer.group === t('overlays'))),
    [layers, t]
  );

  if (overlays.length) {
    layerControlGroups.push(
      <Group
        id='overlayList'
        key='overlays'
        name='overlays'
        onToggle={onToggle}
        title={t('overlays')}
        expanded={openGroups.includes('overlays')}
        titleAddons={renderGroupCheckbox(t('overlays'), true)}
      >
        {overlays.map((layer) => renderLayer(layer, true))}
      </Group>
    );
  }

  const groups = groupBy(
    layers.filter((layer) => !layer.baseLayer && layer.group && layer.group !== t('overlays')),
    'group'
  );

  const sortedNames = Object.keys(groups).sort((a, b) => (a < b ? -1 : 1));
  sortedNames.forEach((groupName) => {
    const layers = groups[groupName];
    if (!layers) {
      return;
    }
    layerControlGroups.push(
      <Group
        id={`layerControlGroup_${groupName}`}
        key={groupName}
        name={groupName}
        onToggle={onToggle}
        title={groupName}
        expanded={openGroups.includes(groupName)}
        titleAddons={renderGroupCheckbox(groupName)}
      >
        {layers.map((layer) => renderLayer(layer, true))}
      </Group>
    );
  });

  const onClose = () => {
    onClickAway?.();
  };

  const renderLayersDrawer = () => {
    const withoutToolbarStyle = {
      top: withoutToolbar ? '0px' : 'unset',
      height: withoutToolbar ? '100%' : undefined,
    };

    return (
      <ClickAwayListener mouseEvent='onMouseDown' touchEvent='onTouchStart' onClickAway={onClose}>
        <Drawer
          open={open}
          anchor={anchor}
          hideBackdrop
          PaperProps={{
            className: classes.drawerPaper,
            id: 'LayerToolbarDrawer',
            style: withoutToolbarStyle,
          }}
          ModalProps={{
            disablePortal: true,
          }}
          variant='persistent'
        >
          {layerControlGroups}
        </Drawer>
      </ClickAwayListener>
    );
  };

  return renderLayersDrawer();
};

export default LayersDrawer;
